import { resolveServiceWorker } from 'shared/channels/ensureServiceWorkerAwake';
import {
  ServiceWorkerHealthMessageType,
  ServiceWorkerHealthReport,
} from 'shared/domain/messages/serviceWorkerHealth';

export enum ServiceWorkerUnavailable {
  noWorker = 'no-worker',
  unresponsive = 'unresponsive',
}

/**
 * Asks the worker for its health report over `ServiceWorker.postMessage`,
 * which starts a stopped worker, unlike a broadcast channel message.
 */
export async function requestServiceWorkerHealthReport(
  timeoutMs: number
): Promise<ServiceWorkerHealthReport | ServiceWorkerUnavailable> {
  if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) {
    return ServiceWorkerUnavailable.noWorker;
  }
  const worker = await resolveServiceWorker(navigator.serviceWorker, timeoutMs);
  if (!worker) {
    return ServiceWorkerUnavailable.noWorker;
  }

  return new Promise((resolve) => {
    const channel = new MessageChannel();
    const finish = (result: ServiceWorkerHealthReport | ServiceWorkerUnavailable): void => {
      clearTimeout(timer);
      channel.port1.close();
      resolve(result);
    };
    const timer = setTimeout(() => finish(ServiceWorkerUnavailable.unresponsive), timeoutMs);
    channel.port1.onmessage = (event: MessageEvent<ServiceWorkerHealthReport>): void =>
      finish(event.data);
    try {
      worker.postMessage({ type: ServiceWorkerHealthMessageType.request }, [channel.port2]);
    } catch {
      // The worker went away (redundant) between resolving and posting.
      finish(ServiceWorkerUnavailable.noWorker);
    }
  });
}
